/**
 * Turn a raw workflow title (English) into a kebab-case slug that satisfies
 * the domain regex on `appName`: `^[a-z][a-z0-9-]{2,63}$`.
 *
 * `CreateWorkflowModal` composes the final appName as
 * `${base}-${YYYYMMDDHHmmss}`. The timestamp is 14 chars + the joining
 * hyphen = 15, so the base is capped at 49 to keep the whole string
 * within the domain's 64-char ceiling.
 *
 * Returns `''` when nothing survives the strip (e.g. the title was
 * `"12345"` — all digits, no leading letter). Callers pass that through
 * as an empty appName, forcing the user to fill it in themselves.
 *
 * Bug fixed 2026-07-21: the previous implementation only replaced spaces
 * with hyphens, so a title like `"BMO RDSP"` produced `"BMO-RDSP-..."`
 * — uppercase letters failed the domain regex and Create returned the
 * "Invalid" error. Sanitization now lowercases + strips non-slug chars.
 */
const MAX_BASE_LENGTH = 49;

export function deriveAppNameBase(rawTitle: string): string {
  return (
    rawTitle
      .toLowerCase()
      .trim()
      // Diacritics — decompose then drop the combining marks so `é` → `e`,
      // `ç` → `c`. Keeps the slug intelligible for accented titles instead
      // of stripping the entire letter later.
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/\s+/g, '-')
      // Anything else that isn't [a-z0-9-] is out — punctuation, symbols,
      // non-Latin scripts. Kept intentionally strict; the domain regex is
      // the source of truth.
      .replace(/[^a-z0-9-]/g, '')
      // Collapse repeated hyphens created by the strips above.
      .replace(/-+/g, '-')
      // First char must be a letter — strip any leading digits / hyphens
      // that would fail `^[a-z]`.
      .replace(/^[^a-z]+/, '')
      .slice(0, MAX_BASE_LENGTH)
      // A slice can leave a trailing hyphen from a collapsed sequence — drop it
      // so the composed string doesn't have `--` at the join.
      .replace(/-+$/, '')
  );
}
